import h, { isTVodProgram, getCountry } from '../../helpers/common';
import f from '../../helpers/formatter';
import { __ } from '../../translations';
import PlayVideoButton from '../elements/PlayVideoButton';
import DetailsButton from '../elements/DetailsButton';
import AddWatchlistButton from '../elements/AddWatchlistButton';
import { Button, FontIcon, Text } from '@intigral/shared';
import { Carousel2, VideoPopup, Program } from '@intigral/ui';
import { SectionHeaderWithPagination } from '@intigral/ui/components/SectionHeaderWithPagination';
import { ButtonTypes, COMPONENT_TYPES } from '../../config/constants';
import { useConfiguration } from '../../hooks/useConfiguration';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { ContentItem } from '../../hooks/useContinueWatching';
import useDetailOverlay from '../../hooks/useDetailOverlay';
import { isFreeOrInactive } from '@intigral/utils/lib/user';
import { getUserInfo } from '../../utils/user';
import { checkShowLockIcon, isSubscriptionLockedContent } from '../../helpers/vod';
import _ from 'lodash';

interface ContinueWatchCarouselProps {
    componentId: string;
    carouselId: string;
    meta: any;
    assets: any[];
    name: string;
}

interface VODDetail {
    guid: string;
    runtime?: number;
    duration?: number;
}

const ContinueWatchCarousel = ({ componentId, carouselId, meta, assets, name }: ContinueWatchCarouselProps) => {
    const { data: config } = useConfiguration();
    const pagingId = `component-${Math.random().toString(36).slice(2, 11)}`;
    const paginationClass = `pagination-${pagingId}`;
    const language = h.getLang();
    const openDetailOverlay = useDetailOverlay();
    const { paidVsFree } = useConfigurationSections();
    const enableLockIcon = isFreeOrInactive(getUserInfo()) && paidVsFree?.lockIconEnable;

    const wrapComponentWithPlayButton = (component: React.ReactElement, item: ContentItem) => {
        return (
            <PlayVideoButton
                className="inner-img"
                buttonType={ButtonTypes.ContinueWatching}
                item={item}
                componentType={COMPONENT_TYPES.CONTINIE_WATCHING}
                componentName={name}
            >
                {component}
            </PlayVideoButton>
        );
    };

    if (!assets || !assets.length) {
        return null;
    }

    const RenderPlayButton = ({ item, isBookmarked }) => {
        const playBtn = wrapComponentWithPlayButton(
            <Button compact>
                {isBookmarked ? __('continue_watching') : __('play_now')}
                <FontIcon name="play-outline" md="smaller" />
            </Button>,
            item
        );
        return playBtn;
    };

    return (
        <div className="Continue-watching-revamped container full-width" id={componentId} data-test-id={carouselId}>
            <SectionHeaderWithPagination
                title={f.getComponentTitle(meta)}
                className={`${paginationClass} paging`}
                seeAllText={__('home_carousel_see_all')}
            />
            <Carousel2
                dir={language === 'ar' ? 'rtl' : 'ltr'}
                swiperSettings={{
                    slidesPerGroup: 1,
                    virtual: false,
                    pagination: false,
                    autoplay: false,
                    loop: false,
                    slidesPerView: 1,
                    breakpoints: {
                        560: {
                            slidesPerView: 2,
                        },
                        924: {
                            slidesPerView: 3,
                        },
                        1212: {
                            slidesPerView: 3,
                        },
                        1500: {
                            slidesPerView: 4,
                        },
                    },
                }}
            >
                {assets.map((asset, index) => {
                    const { guid, episodeNumber, seasonNumber } = asset;
                    const poster = h.getImagizerUrl(
                        asset.image_landscape || asset.image || '',
                        config?.imagizer?.components_images.REWIND_ITEM
                    );
                    const heading = language === 'en' ? asset?.textEn : asset?.textAr;
                    const assetDetails = {
                        info: [
                            asset.year ? asset.year : null,
                            asset.genre ? (Array.isArray(asset.genre) ? asset.genre.map((g) => __(g)) : []) : null,
                            asset.programType !== 'LINEAR' && asset.startTime && asset.endTime
                                ? `${f.formatRewindDate(asset.startTime, true)} - ${f.formatRewindDate(
                                      asset.endTime,
                                      false
                                  )}`
                                : null,
                            episodeNumber ? __(`tv_episode_number_short`) + ' ' + episodeNumber : null,
                            seasonNumber ? __(`tv_episode_season_number_short`) + ' ' + seasonNumber : null,
                        ],
                        rating:
                            asset.ratings && typeof asset.ratings === 'string'
                                ? __(`rating_${asset.ratings.toLowerCase()}`)
                                : null,
                    };

                    const isBookmarked = asset?.isBookmarked;
                    const watchlistBtn =
                        asset?.programType !== 'LINEAR' ? (
                            <AddWatchlistButton item={asset} type={ButtonTypes.VodeItem} />
                        ) : (
                            <AddWatchlistButton item={{ ...asset, category: 'linear' }} type={ButtonTypes.VodeItem} />
                        );

                    const isLocked = checkShowLockIcon(asset?.subscriptionGuids ?? []) && enableLockIcon;
                    const isSubscriptionLocked = isSubscriptionLockedContent(
                        asset,
                        (!isTVodProgram(asset) && isLocked) || asset?.isLocked
                    );

                    return (
                        <div key={guid ?? index} className="inner" id={guid} data-test-id={guid}>
                            <VideoPopup
                                cover=""
                                picture={poster}
                                heading={heading}
                                rating={assetDetails.rating}
                                info={assetDetails.info}
                                play={<RenderPlayButton item={asset} isBookmarked={isBookmarked} />}
                                watchlistButton={watchlistBtn}
                                page={
                                    asset?.programType !== 'LINEAR' && !isSubscriptionLocked
                                        ? {
                                              onClick: () => {
                                                  openDetailOverlay(asset);
                                              },
                                          }
                                        : undefined
                                }
                            >
                                <Program
                                    program={{
                                        poster,
                                        text: heading,
                                        progress: asset.percentageNumber,
                                    }}
                                    showProgress={true}
                                    assetDetails={assetDetails}
                                    CTAs={[
                                        wrapComponentWithPlayButton(
                                            <FontIcon name="play" size="x-large" md="x-large" />,
                                            asset
                                        ),
                                    ]}
                                    withBlur
                                    drawerCTAs={[
                                        <DetailsButton
                                            key={`details-${guid ?? index}`}
                                            buttonType={ButtonTypes.ContinueWatching}
                                            item={asset}
                                            componentType={COMPONENT_TYPES.CONTINIE_WATCHING}
                                            componentName={name}
                                            notClickable={isTVodProgram(asset)}
                                            className=""
                                            meta={{}}
                                        >
                                            <Button fullWidth compact secondary>
                                                <FontIcon name="info" size="small" />{' '}
                                                <span>
                                                    <Text as="p" variant="text3">
                                                        {__('more_info')}
                                                    </Text>
                                                </span>
                                            </Button>
                                        </DetailsButton>,
                                        <AddWatchlistButton
                                            key={`watchlist-${guid ?? index}`}
                                            item={asset}
                                            type={ButtonTypes.VodeItem}
                                        >
                                            <span>{__('mylist_button_label')}</span>
                                        </AddWatchlistButton>,
                                    ]}
                                    {...asset}
                                />
                            </VideoPopup>
                        </div>
                    );
                })}
            </Carousel2>
        </div>
    );
};
export default ContinueWatchCarousel;
